/**
 * AppRouter — route table plus OAuth callback interception.
 *
 * Phase 3: every authenticated user-facing route is nested inside one shell
 * (`UserShell`), which owns the chrome and the workspace state. `/admin` stays
 * outside it, untouched, inside the `legacy-admin-scope` boundary.
 *
 * The `session_id` check happens during render, not in an effect: an effect
 * runs after the first paint, by which time ProtectedRoute would already have
 * redirected to /login and thrown away the one-time id.
 */
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import App from "@/App";
import { AuthCallback } from "@/components/AuthCallback";
import { LoginScreen } from "@/components/LoginScreen";
import { AdminRoute, ProtectedRoute } from "@/components/ProtectedRoute";
import { UserShell } from "@/components/UserShell";
import { useAuth } from "@/contexts/AuthContext";

// PERF-003 — the admin console (all its tabs) and the profile page are
// code-split: first paint of the user app no longer downloads them.
const AdminConsole = lazy(() =>
  import("@/components/AdminConsole").then((m) => ({ default: m.AdminConsole })),
);
const ProfilePage = lazy(() => import("@/components/ProfilePage"));
const PortfoliosPage = lazy(() => import("@/components/PortfoliosPage"));

function RouteFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center text-content-muted text-sm"
      data-testid="route-loading"
    >
      Caricamento…
    </div>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Already signed in: skip the door.
  if (user) return <Navigate to="/" replace />;
  return <LoginScreen />;
}

function AdminConsoleRoute() {
  const { isAdmin } = useAuth();
  // UI FOUNDATION Phase 1 — `legacy-admin-scope` re-declares the design tokens
  // with the console's current values: the Admin Console is migrated in its own
  // round and must not change appearance when the user switches theme.
  return (
    <div className="app-container legacy-admin-scope" data-testid="app-container">
      <div className="app-content-wrapper">
        <AdminConsole authorized={isAdmin} />
      </div>
    </div>
  );
}

export function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/admin"
          element={<AdminRoute><AdminConsoleRoute /></AdminRoute>}
        />
        <Route element={<ProtectedRoute><UserShell /></ProtectedRoute>}>
          <Route path="/" element={<App />} />
          <Route path="/portfolios" element={<PortfoliosPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default AppRouter;
